import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';

interface LanguageSelectorProps {
	className?: string;
}

const languages = [
	{ code: 'en', name: 'English', flag: '🇺🇸' },
	{ code: 'el', name: 'Ελληνικά', flag: '🇬🇷' }
];

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
	className = ''
}) => {
	const { i18n, t } = useTranslation('common');
	const [isOpen, setIsOpen] = React.useState(false);

	const handleLanguageChange = (languageCode: string) => {
		i18n.changeLanguage(languageCode);
		setIsOpen(false);
	};

	const currentLanguage =
		languages.find(lang => lang.code === i18n.language) || languages[0];

	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Element;
			if (!target.closest('.language-selector')) {
				setIsOpen(false);
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, []);

	return (
		<div className={`relative language-selector ${className}`}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center space-x-2 p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
				title={t('language.select_language')}
			>
				<Languages className="w-5 h-5" />
				<span className="hidden sm:inline text-sm font-medium">
					{currentLanguage.flag} {currentLanguage.name}
				</span>
			</button>

			{isOpen && (
				<div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
					<div className="py-1">
						<div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
							{t('language.select_language')}
						</div>
						{languages.map(language => (
							<button
								key={language.code}
								onClick={() =>
									handleLanguageChange(language.code)
								}
								className={`
                  w-full flex items-center justify-between px-3 py-2 text-sm
                  ${
						i18n.language === language.code
							? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
							: 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
					}
                  transition-colors
                `}
							>
								<div className="flex items-center space-x-3">
									<span className="text-lg">
										{language.flag}
									</span>
									<span className="font-medium">
										{language.name}
									</span>
								</div>
								{i18n.language === language.code && (
									<Check className="w-4 h-4 text-purple-600 dark:text-purple-400" />
								)}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
